/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

// Íconos
import { ReactComponent as ProfileIcon } from "../../assets/profile-circle.svg";
import { ReactComponent as IconEditUser } from "@assets/Icons/EditUser.svg";
import { ReactComponent as IconChangelog } from "@assets/Icons/IconChangelog.svg";
import { ReactComponent as IconLogout } from "@assets/Icons/Logout.svg";
import { ReactComponent as IconResetPassword } from "@assets/Icons/ResetPassword.svg";

// Otros imports
import ThemeSwitcher from "../../../theme/components/ThemeSwitcher";
import { useAppDispatch, useAppSelector } from "../../../shared/redux/hooks";
import { getPageTitle } from "./helpers/getPageTitle";
import PartnerCustomerDropdown from "./components/PartnerCustomerDropdown";
import RenderTabsByRoute from "./helpers/getRenderTabsByRoute";
import { KontrolMenu } from "./components/Tabs/Kontrol/KontrolTabs";
import ChangelogModal from "./components/ChangelogModal";
import { clearSelection } from "@redux/slices/tenantSlice";
import localStorage from "redux-persist/lib/storage";
import { getCurrentView } from "./helpers/getCurrentView";
import { openEditor } from "@redux/slices/userEditorSlice";
import { parseAxiosError } from "@shared/components/utils/apiErrorParser";
import { useUserMgmt } from "@shared/hooks/useUserMgmt";
import { useAlerts } from "@shared/hooks/useAlerts";

const LOGOUT_REDIRECT_URL =
  process.env.REACT_APP_LOGOUT_REDIRECT_URI || "http://localhost:3002";

export default function SideNav() {
  const dispatch = useAppDispatch();
  const { logout, isLoading, isAuthenticated, getAccessTokenSilently } =
    useAuth0();
  const location = useLocation();
  const pageTitle = getPageTitle(location.pathname);

  // Obtenemos TenantType y userProfile desde Redux
  const tenantType = useAppSelector((state) => state.userProfile.TenantType);
  const userProfile = useAppSelector((state) => state.userProfile);

  const userName = `${userProfile.firstName} ${userProfile.lastName}`.trim();
  const userEmail = userProfile.email;

  const { resetPassword } = useUserMgmt();
  const { confirm, alert, Alert } = useAlerts();

  // Funciones para Logout y Edit
  function handleLogout() {
    // 1)Clear the Redux slice immediately
    dispatch(clearSelection());
    // 2) synchronously drop the persisted storage key
    //    (exported `storage` is localStorage under the hood)
    localStorage.removeItem("persist:root");
    // 3) now kick off Auth0’s logout+redirect
    logout({ logoutParams: { returnTo: LOGOUT_REDIRECT_URL } });
  }

  // Menú Kontrol
  // const { canAccessAlerts } = useAppSelector(
  //   (state: RootState) => state.userProfile
  // );
  const isKontrolRoute = location.pathname.startsWith("/kontrol");
  const { KontrolDropdowns } = KontrolMenu({ tenantType });
  const selection = useAppSelector((s) => s.selection);
  const { customerId, tenantId, partnerId } = selection;

  //Banner for user type next to dropdowns
  const currentView = getCurrentView(tenantType, selection.partnerId, selection.tenantId);
  // 🎨 Tailwind styles based on current view
  const bannerColorClass =
    currentView === "PARTNER"
      ? "bg-blue-200 text-blue-900"
      : currentView === "CUSTOMER"
      ? "bg-green-100 text-green-800"
      : "bg-gray-200 text-gray-700"; 

  const viewLabel =
    currentView === "PARTNER"
      ? "Partner View"
      : currentView === "CUSTOMER"
      ? "Customer View"
      : "";
  const idLabel =
    currentView === "CUSTOMER" && selection.customerId
      ? `ID: ${selection.customerId}`
      : "";
  const showCustomerIdTag =
      currentView === "CUSTOMER" &&
      !!customerId &&
      tenantId !== partnerId;

  ////////////////////

  // Detect any “/kontrol/admin-center” page
  const isAdminCenterRoute = location.pathname.startsWith(
    "/kontrol/admin-center"
  );

  // Estados para perfil y modal de Changelog
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setProfileMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Close when clicking/tapping outside
  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (!profileMenuOpen) return;
      const target = e.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return;
      setProfileMenuOpen(false);
    };
    window.addEventListener("pointerdown", onPointerDown, { capture: true });
    return () =>
      window.removeEventListener(
        "pointerdown",
        onPointerDown as any,
        { capture: true } as any
      );
  }, [profileMenuOpen]);


  // Cerrar menú de perfil con "Esc"
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setProfileMenuOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Abrir / cerrar Changelog
  const handleOpenChangelog = () => {
    setChangelogOpen(true);
    setProfileMenuOpen(false);
  };
  const handleCloseChangelog = () => {
    setChangelogOpen(false);
  };

  async function handleResetPasswordNav() {
    const email = userProfile?.email;
    if (!email) return;
    const ok = await confirm({
      title: `Reset password for ${email}?`,
      confirmLabel: "Yes, reset!",
      cancelLabel: "Cancel",
      variant: "warning",
    });
    if (!ok) return;

    try {
      await resetPassword(email);
      await alert({
        title: "Password Reset!",
        description: "An email to reset the password has been sent.",
        confirmLabel: "OK",
        variant: "success",
      });
    } catch (e) {
      const { message } = parseAxiosError(e);
      await alert({
        title: "Error!",
        description: message || "Failed to send password reset email.",
        confirmLabel: "OK",
        variant: "error",
      });
    }
  }

  return (
    <>
          {Alert}
      <header
        className="
          w-full
          bg-semantic-bg-primary
          text-semantic-text-secondary
          shadow-md
          px-6
          py-3
          flex
          flex-col
          gap-2
        "
      >
        <div
          className="
            flex items-center 
            justify-between 
            pb-2
            -mx-6 px-6
            border-b border-semantic-border-secondary
          "
        >
          {/* IZQUIERDA */}
          <div className="flex items-center gap-6">
            {/* Added this validation for the admin-center routes */}
            {!isAdminCenterRoute && (
              <>
                <PartnerCustomerDropdown
                  tenantType={tenantType}
                  isLoading={isLoading}
                  isAuthenticated={isAuthenticated}
                  getAccessTokenSilently={getAccessTokenSilently}
                />
                {(tenantType === "GLOBAL_ADMIN" || tenantType === "PARTNER") && (
                  <div>
                    <div className={`${bannerColorClass} px-8 py-2 mr-4 rounded-md text-sm font-medium inline-block`}>
                      <strong>{viewLabel}</strong>
                    </div>
                    {showCustomerIdTag && (
                      <div
                        className={`${bannerColorClass} px-3 py-1.5 rounded-md text-sm font-medium inline-block`}
                        title="Customer external ID"
                      >
                        <strong>ID: </strong>{customerId}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* DERECHA: Botón de Perfil */}
          <div className="flex items-center gap-4">
            {/* Muestra cosas extra si la ruta inicia con /kontrol */}
            {/* {isKontrolRoute && !isAdminCenterRoute && canAccessAlerts && (
              <KontrolDropdowns />
            )} */}
            <div className="relative">
              <button
                ref={buttonRef}
                onClick={() => setProfileMenuOpen((o) => !o)}
                className="
                  flex items-center justify-center
                  h-10 w-10
                  rounded-full
                  hover:bg-semantic-state-active
                  transition-colors
                "
              >
                {/* Ícono de perfil, un poco más grande y con stroke para engrosar líneas */}
                <ProfileIcon
                  className="
                    h-9 w-9
                    text-semantic-text-secondary
                    dark:text-gray-500
                    stroke-[1.8]
                  "
                />
              </button>

              {profileMenuOpen && (
                <div
                  id="profile-menu"
                  ref={menuRef}
                  role="menu"
                  className="
                    absolute
                    right-0
                    mt-2
                    w-48
                    bg-semantic-bg-primary
                    rounded-md
                    shadow-md
                    border border-semantic-border-secondary
                    z-50
                  "
                >
                  {/* Nombre + correo */}
                  <div
                    className="
                      px-4
                      py-2
                      border-b
                      border-semantic-border-secondary
                    "
                  >
                    <p className="text-sm font-semibold text-semantic-text-primary">
                      {userName}
                    </p>
                    <p className="text-xs text-semantic-text-secondary">
                      {userEmail}
                    </p>
                  </div>

                  <ul className="py-1 text-sm text-semantic-text-primary">
                    {/* Theme Switcher */}
                    <li
                      className="
                        px-4 py-2
                        hover:bg-semantic-state-active
                        transition-colors
                        cursor-pointer
                      "
                    >
                      <ThemeSwitcher />
                    </li>

                    {/* Reset Password con ícono */}
                    {userProfile?.id?.startsWith("auth0|") && (
                      <li
                        className="
                          border-t border-semantic-border-secondary
                          px-4 py-2
                          hover:bg-semantic-state-active
                          cursor-pointer
                          transition-colors
                          flex items-center gap-2
                        "
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleResetPasswordNav();
                        }}
                      >
                        <IconResetPassword
                          className="
                            h-5 w-5
                            text-semantic-text-secondary
                            dark:text-gray-500
                          "
                        />
                        <span className="font-medium text-semantic-text-secondary">Reset Password</span>
                      </li>
                    )}

                    {/* Edit User con ícono */}
                    <li
											className="
                        border-t border-semantic-border-secondary
                        px-4 py-2
                        hover:bg-semantic-state-active
                        cursor-pointer
                        transition-colors
                        flex items-center gap-2
                      "
                      onClick={() => {
                        if (userProfile?.id) dispatch(openEditor(userProfile.id));
                      }}
										>
											<IconEditUser
												className="
                          h-5 w-5
                          text-semantic-text-secondary
                          dark:text-gray-500
                        "
											/>
											<span className="font-medium text-semantic-text-secondary">Edit User</span>
										</li>

                    {/* Changelog + versión */}
                    {/* <li
											className="
                        border-t border-semantic-border-secondary
                        px-4 py-2
                        hover:bg-semantic-state-active
                        cursor-pointer
                        transition-colors
                        flex items-center gap-2
                      "
											onClick={handleOpenChangelog}
										>
											<IconChangelog
												className="
                          h-7 w-7
                          text-semantic-text-secondary
                          dark:text-gray-500
                        "
											/>
											<div className="flex flex-col">
												<span>Changelog</span>
												<span className="text-xs text-semantic-text-secondary">
													v2.3.0
												</span>
											</div>
										</li> */}

                    {/* Sign Out con ícono de Logout */}
                    <li
                      className="
                        border-t border-semantic-border-secondary
                        px-4 py-2
                        hover:bg-semantic-state-active
                        cursor-pointer
                        transition-colors
                        flex items-center gap-2
                      "
                      onClick={handleLogout}
                    >
                      <IconLogout
                        className="
                          h-5 w-5
                          text-semantic-text-secondary
                          dark:text-gray-500
                        "
                      />
                      <span className="font-medium text-semantic-text-secondary">Sign Out</span>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Título principal */}
        <h2
          className="
            text-xl
            font-medium
            text-[var(--semantic-text-secondary)]
          "
        >
          {pageTitle}
        </h2>
        <RenderTabsByRoute />
        {/* 🔑 Render this so confirm/alert can appear and resolve */}

      </header>


      {/* Modal de Changelog */}
      <ChangelogModal isOpen={changelogOpen} onClose={handleCloseChangelog} />
    </>
  );
}
